<template>
  <v-container v-if="isLoading" fluid class="ma-0 pa-2">
    <v-row class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0"> <loading-spinner /> </v-col>
    </v-row>
  </v-container>

  <v-container v-else fluid class="ma-0 pa-0 mt-2">
    <v-container fluid class="ma-0 pa-0 mt-2">
      <v-chip
        v-if="!isCollection"
        label
        icon
        :color="helpChipColor"
        class="font-weight-bold white--text ma-0 pa-3 mb-2"
      >
        <v-icon center class="ma-0 pa-0 mr-2 white--text"
          >mdi-alert-circle</v-icon
        >
        {{ helpChipText }}
      </v-chip>

      <visit-variables-toolbar
        :interactive="!isCollection && hasVariablesSelected"
        @hideUnselectedVars="hideUnselectedVarsChanged"
      />

      <visit-times-table class="mb-2" />

      <bubble-chart
        ref="bubble_chart"
        :unselected-var-opacity="0.3"
        :selected-var-opacity="0.8"
        :hide-unselected-vars="hideUnselectedVars"
        :interactive="!isCollection && hasVariablesSelected"
      ></bubble-chart>

      <v-spacer></v-spacer>

      <v-tooltip top color="primary">
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            v-if="!isCollection"
            :disabled="!hasVariablesSelected"
            class="primary text--white ma-0 ml-2 px-2 py-0"
            v-on="{ ...tooltip }"
            @click="autoSetVisits"
            >Auto Set Visits</v-btn
          >
        </template>
        <span class="subtitle-1"
          >Automatically set first and last visit for all variables.</span
        >
      </v-tooltip>
    </v-container>

    <v-dialog
      v-model="autoSetDialog"
      persistent
      scrollable
      max-width="50%"
      min-height="50%"
    >
      <v-card class="rounded-lg" style="border: 3px solid #3f51b5;">
        <v-card-title color="white" class="ma-0 pa-2" primary-title>
          <span class="primary--text text--darken-3 title"
            >Automatically Set First & Last Visits</span
          >
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4">
          <v-list>
            <v-list-item v-for="(s, i) in autoSetSteps" :key="`as-${i}`">
              <v-list-item-content> {{ s.title }} </v-list-item-content>
              <v-list-item-avatar>
                <v-icon v-if="i < autoSetSteps.length - 1">done</v-icon>
              </v-list-item-avatar>
            </v-list-item>
          </v-list>

          <v-container v-if="autoSetEstMaxStudySize">
            <v-row>
              <v-col cols="10">
                <v-slider
                  v-model="autoSetMinStudySize"
                  class="pt-3"
                  label="Minimum study population size"
                  step="1"
                  thumb-label="always"
                  min="1"
                  :max="autoSetEstMaxStudySize"
                  @change="doAutoSet(autoSetMinStudySize)"
                >
                </v-slider>
              </v-col>

              <v-col cols="2">
                <v-text-field
                  v-model.number="autoSetMinStudySize"
                  class="center-text pa-0 ma-0"
                  type="number"
                  @change="doAutoSet(autoSetMinStudySize)"
                ></v-text-field>
                ({{
                  Math.round(
                    (autoSetMinStudySize / autoSetEstMaxStudySize) * 100
                  )
                }}%)
              </v-col>
            </v-row>

            <v-row class="ma-0 pa-0 pt-4">
              <v-col cols="12" class="ma-0 pa-0">
                Move the slider until the study population size and average
                elapsed time (from the selected first visit to last visit) shown
                below are acceptable and then click on "SET VISITS":
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-chip
                  class="title ml-3"
                  :color="getNumSubjectsColor(numSelectedSubjects)"
                  :text-color="getNumSubjectsTextColor(numSelectedSubjects)"
                  >Study Population - {{ numSelectedSubjects }}</v-chip
                >

                <v-chip class="title ml-3" color="deep-orange lighten-4"
                  >Average Elapsed Time -
                  {{ avgTimeBetweenVisits | formatTime }}
                </v-chip>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="cancelAutoSet()">
            <v-icon left>close</v-icon> Cancel
          </v-btn>

          <v-btn
            class="primary white--text ma-0 px-2 mx-2"
            :disabled="!autoSetEnableCloseDialog"
            @click="closeAutoSetDialog()"
          >
            SET VISITS
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { state, actions } from '@/store/modules/datasetSummary/types';
import { format } from 'd3-format';
import BubbleChart from '@/components/DatasetSummary/BubbleChart.vue';
import VisitTimesTable from '@/components/DatasetSummary/VisitTimesTable.vue';
import VisitVariablesToolbar from '@/components/DatasetSummary/VisitVariablesToolbar.vue';
import { getNumSubjectsColor, getNumSubjectsTextColor } from '@/utils/colors';
import { estimateMaxSubjects, estimateMaxVisits } from '@/utils/helpers';

export default {
  components: {
    BubbleChart,
    VisitTimesTable,
    VisitVariablesToolbar,
  },
  filters: {
    formatTime(tsecs) {
      var tdays = tsecs / (3600 * 24);
      var tyears = tdays / 365.25;
      if (tdays <= 60) {
        return format('.1f')(tdays) + ' days';
      } else {
        return format('.1f')(tyears) + ' year(s)';
      }
    },
  },
  data() {
    return {
      isLoading: true,
      autoSetDialog: false,
      autoSetEnableCloseDialog: false,
      autoSetEnableSetButton: false,
      autoSetSteps: [],
      autoSetEstMaxStudySize: null,
      autoSetMinStudySize: 1,
      autoSetDefaultFrac: 0.7,
      subject_variable_visits: null,
      getNumSubjectsColor: getNumSubjectsColor,
      getNumSubjectsTextColor: getNumSubjectsTextColor,
      avgTimeBetweenVisits: null,
      savedVisits: null,
      hideUnselectedVars: true,
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      selectedVariables: state.SELECTED_VARIABLES,
      firstVisits: state.FIRST_VISITS,
      lastVisits: state.LAST_VISITS,
      numSelectedSubjects: state.NUM_SELECTED_SUBJECTS,
      timesBetweenVisits: state.TIMES_BETWEEN_VISITS,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
      isCollection: state.IS_COLLECTION,
      enableVariableSelection: state.ENABLE_VARIABLE_SELECTION,
    }),
    observationVariables() {
      return this.selectedVariables.filter(svar => svar.type == 'observation');
    },
    hasVariablesSelected() {
      return this.observationVariables.length > 0;
    },
    helpChipColor() {
      if (this.selectedVariables.length > 0) {
        return '#4caf50';
      } else {
        return '#F83008';
      }
    },
    helpChipText() {
      if (this.enableVariableSelection) {
        if (this.selectedVariables.length > 0) {
          return 'Go to "Variables" tab to hide variables displayed below.';
        } else {
          return 'No variables selected';
        }
      } else {
        return 'Click "Enable variable selection" to hide variables displayed below.';
      }
    },
  },
  watch: {
    timesBetweenVisits(tbv) {
      if (tbv != null) {
        tbv.forEach(t => {
          if (t['study_name'] == '_all') {
            this.avgTimeBetweenVisits = t['avg_time_secs'];
          }
        });
      }
    },
  },
  async created() {
    this.isLoading = true;

    await this.fetchVisitSummaries();

    this.isLoading = false;
  },
  methods: {
    ...mapActions('datasetSummary', {
      setFirstVisits: actions.SET_FIRST_VISITS,
      setLastVisits: actions.SET_LAST_VISITS,
      fetchVisitSummaries: actions.FETCH_VISIT_SUMMARIES,
      getStudySubjectVariableVisits: actions.GET_STUDY_SUBJECT_VARIABLE_VISITS,
    }),
    hideUnselectedVarsChanged(newval) {
      this.hideUnselectedVars = newval;
    },
    setFirstVisit(varId, visit) {
      var newFirstVisits = {};
      Object.keys(this.firstVisits).forEach(id => {
        newFirstVisits[id] = this.firstVisits[id];
      });
      newFirstVisits[varId] = visit;
      this.setFirstVisits(newFirstVisits);
    },
    setLastVisit(varId, visit) {
      var newLastVisits = {};
      Object.keys(this.lastVisits).forEach(id => {
        newLastVisits[id] = this.lastVisits[id];
      });
      newLastVisits[varId] = visit;
      this.setLastVisits(newLastVisits);
    },
    async doAutoSet(min_size) {
      var ovars = this.observationVariables;
      var emv = estimateMaxVisits(
        this.subject_variable_visits,
        ovars,
        'event',
        min_size
      );

      // set first and last visits
      var events = this.subject_variable_visits['visits']['event'];

      emv['visits'].forEach(vis => {
        this.setFirstVisit(vis[1], events[vis[2]]['visit_event']);
        this.setLastVisit(vis[1], events[vis[3]]['visit_event']);
      });

      // move visit handles to agree with consensus
      this.$refs.bubble_chart.setVisitHandle(true);
      this.$refs.bubble_chart.setVisitHandle(false);
      this.autoSetEnableCloseDialog = true;
    },
    saveVisits() {
      this.savedVisits = { first: {}, last: {} };
      Object.keys(this.firstVisits).forEach(vis => {
        this.savedVisits['first'][vis] = this.firstVisits[vis];
        this.savedVisits['last'][vis] = this.lastVisits[vis];
      });
    },
    restoreSavedVisits() {
      if (this.savedVisits != null) {
        Object.keys(this.savedVisits['first']).forEach(vis => {
          this.setFirstVisit(vis, this.savedVisits['first'][vis]);
          this.setLastVisit(vis, this.savedVisits['last'][vis]);
        });
        // move visit handles to agree with consensus
        this.$refs.bubble_chart.setVisitHandle(true);
        this.$refs.bubble_chart.setVisitHandle(false);
      }
      this.savedVisits = null;
    },
    async autoSetVisits() {
      this.autoSetSteps = [];
      this.saveVisits();
      this.autoSetDialog = true;

      // get subject visit info
      if (!this.subject_variable_visits) {
        this.autoSetSteps.push({ title: 'Loading subject visit data' });
        this.subject_variable_visits = await this.getStudySubjectVariableVisits();
      }

      // run standard routine to get estimate of the max possible study dataset size
      var ovars = this.observationVariables;
      var ems = estimateMaxSubjects(
        this.subject_variable_visits,
        ovars,
        'event'
      );

      this.autoSetSteps.push({
        title:
          'Estimated maximum study population size for the selected variables: ' +
          ems['counts']['all'],
      });

      this.autoSetEstMaxStudySize = ems['counts']['all'];

      // compute minimum number of subjects as fraction of total
      var defaultMinSubjects = Math.round(
        this.autoSetDefaultFrac * ems['counts']['all']
      );

      this.autoSetSteps.push({
        title:
          'Set default minimum study population size to ' +
          this.autoSetDefaultFrac * 100 +
          '% of estimated maximum (' +
          defaultMinSubjects +
          ')',
      });
      this.autoSetMinStudySize = defaultMinSubjects;
      this.doAutoSet(defaultMinSubjects);

      this.autoSetSteps.push({
        title: 'Select desired minimum study population size: ',
      });
    },
    async cancelAutoSet() {
      this.restoreSavedVisits();
      this.closeAutoSetDialog();
    },
    async closeAutoSetDialog() {
      this.autoSetDialog = false;
    },
  },
};
</script>

<style scoped></style>
